import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { Box, Typography, Chip, Table, TableBody, TableCell, TableRow, TableHead } from '@mui/material';
import { extractValues, skewness, kurtosis, kde } from '../statistics';
import { useTimelineControls } from '../TimelineContext';

const COLORS = ['#2065D1', '#FF6C40'];

function buildKdeSeries(rec, recLabel, sensorKey, startTime, endTime) {
  if (!rec) return null;
  const vals = extractValues(rec, sensorKey, startTime, endTime);
  if (vals.length < 4) return null;
  const { points } = kde(vals, 80);
  return {
    label: recLabel || rec.name || 'Recording',
    points,
    stats: { skewness: skewness(vals), kurtosis: kurtosis(vals), n: vals.length },
  };
}

export default function DistributionWidget({ recordings = [], recordingMetadata = {}, widgetConfig }) {
  const { window: timeWindow } = useTimelineControls();
  const recId1 = widgetConfig?.recId1 || '';
  const recId2 = widgetConfig?.recId2 || '';
  const sensorKey = widgetConfig?.sensorKey || '';

  const rec1 = recordings.find((r) => r.id === recId1);
  const rec2 = recordings.find((r) => r.id === recId2);

  const results = useMemo(() => {
    if (!sensorKey) return null;
    const start = timeWindow?.start;
    const end = timeWindow?.end;
    const r1 = buildKdeSeries(rec1, rec1 && (recordingMetadata[rec1.id]?.label || rec1.name), sensorKey, start, end);
    const r2 = buildKdeSeries(rec2, rec2 && (recordingMetadata[rec2.id]?.label || rec2.name), sensorKey, start, end);
    if (!r1 && !r2) return null;
    return { r1, r2 };
  }, [rec1, rec2, sensorKey, timeWindow, recordingMetadata]);

  const fmtVal = (v) => (Number.isNaN(Number(v)) ? '\u2014' : v.toFixed(4));

  // Build ApexCharts config for KDE line plot
  const chartConfig = useMemo(() => {
    if (!results) return null;
    const series = [];
    if (results.r1) {
      series.push({ name: results.r1.label, data: results.r1.points.map((p) => [p.x, p.y]) });
    }
    if (results.r2) {
      series.push({ name: results.r2.label, data: results.r2.points.map((p) => [p.x, p.y]) });
    }
    const options = {
      chart: { type: 'area', toolbar: { show: false }, animations: { enabled: false }, sparkline: { enabled: false } },
      stroke: { curve: 'smooth', width: 2 },
      fill: { type: 'gradient', gradient: { opacityFrom: 0.3, opacityTo: 0.05, shadeIntensity: 1 } },
      colors: COLORS.slice(0, series.length),
      xaxis: { type: 'numeric', labels: { formatter: (v) => Number(v).toFixed(1), style: { fontSize: '9px' } }, tickAmount: 6 },
      yaxis: { labels: { formatter: (v) => v.toFixed(4), style: { fontSize: '9px' } }, title: { text: 'Density', style: { fontSize: '10px' } } },
      tooltip: { x: { formatter: (v) => Number(v).toFixed(2) }, y: { formatter: (v) => v.toFixed(6) } },
      dataLabels: { enabled: false },
      legend: { show: series.length > 1, position: 'top', fontSize: '10px' },
      grid: { padding: { left: 10, right: 10, top: 0, bottom: 0 } },
    };
    return { series, options };
  }, [results]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {results ? (
        <Box sx={{ flex: 1, overflow: 'auto', px: 1 }}>
          {timeWindow && <Chip label="Windowed" size="small" color="info" variant="outlined" sx={{ mb: 0.5, height: 18, fontSize: '0.6rem' }} />}

          {/* KDE density line plot */}
          {chartConfig && (
            <Box sx={{ height: '55%', minHeight: 100 }}>
              <ReactApexChart type="area" series={chartConfig.series} options={chartConfig.options} height="100%" />
            </Box>
          )}

          {/* Statistics table */}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ py: 0.25, fontSize: '0.7rem', fontWeight: 'bold' }}>Measure</TableCell>
                {results.r1 && <TableCell sx={{ py: 0.25, fontSize: '0.7rem', fontWeight: 'bold' }} align="right">{results.r1.label}</TableCell>}
                {results.r2 && <TableCell sx={{ py: 0.25, fontSize: '0.7rem', fontWeight: 'bold' }} align="right">{results.r2.label}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={{ py: 0.25, fontSize: '0.7rem' }}>Skewness</TableCell>
                {results.r1 && (
                  <TableCell sx={{ py: 0.25, fontSize: '0.7rem' }} align="right">
                    {fmtVal(results.r1.stats.skewness.value)}
                    <Typography variant="caption" display="block" color="text.secondary">{results.r1.stats.skewness.interpretation}</Typography>
                  </TableCell>
                )}
                {results.r2 && (
                  <TableCell sx={{ py: 0.25, fontSize: '0.7rem' }} align="right">
                    {fmtVal(results.r2.stats.skewness.value)}
                    <Typography variant="caption" display="block" color="text.secondary">{results.r2.stats.skewness.interpretation}</Typography>
                  </TableCell>
                )}
              </TableRow>
              <TableRow>
                <TableCell sx={{ py: 0.25, fontSize: '0.7rem' }}>Kurtosis</TableCell>
                {results.r1 && (
                  <TableCell sx={{ py: 0.25, fontSize: '0.7rem' }} align="right">
                    {fmtVal(results.r1.stats.kurtosis.value)}
                    <Typography variant="caption" display="block" color="text.secondary">{results.r1.stats.kurtosis.interpretation}</Typography>
                  </TableCell>
                )}
                {results.r2 && (
                  <TableCell sx={{ py: 0.25, fontSize: '0.7rem' }} align="right">
                    {fmtVal(results.r2.stats.kurtosis.value)}
                    <Typography variant="caption" display="block" color="text.secondary">{results.r2.stats.kurtosis.interpretation}</Typography>
                  </TableCell>
                )}
              </TableRow>
              <TableRow>
                <TableCell sx={{ py: 0.25, fontSize: '0.7rem' }}>Sample Size</TableCell>
                {results.r1 && <TableCell sx={{ py: 0.25, fontSize: '0.7rem' }} align="right">n={results.r1.stats.n}</TableCell>}
                {results.r2 && <TableCell sx={{ py: 0.25, fontSize: '0.7rem' }} align="right">n={results.r2.stats.n}</TableCell>}
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Select recording(s) and a sensor
          </Typography>
        </Box>
      )}
    </Box>
  );
}
